import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  Search, 
  RefreshCw, 
  ArrowUpDown,
  AlertCircle,
  ExternalLink,
  Database,
  Loader2,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import { exportStyledXlsx } from '../lib/excelExport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to convert text to Title Case
const toTitleCase = (str) => {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

export default function Mismatch() {
  const { token, api } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("kyc_mismatch");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "difference", direction: "desc" });
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded rows
  const [mismatchTypeFilter, setMismatchTypeFilter] = useState("needs_attention"); // auto_reconcile, needs_attention, or no_pan

  // ============ Missing Data (Holdings) State ============
  const [holdings, setHoldings] = useState([]);
  const [holdingsLoading, setHoldingsLoading] = useState(true);
  const [holdingsSearchTerm, setHoldingsSearchTerm] = useState("");
  const [holdingsActiveFilter, setHoldingsActiveFilter] = useState(null); // null = no filter active
  const [holdingsStats, setHoldingsStats] = useState({});
  const [holdingsPage, setHoldingsPage] = useState(1);
  const [holdingsPageSize] = useState(50);
  const [holdingsTotalCount, setHoldingsTotalCount] = useState(0);
  const [holdingsSortField, setHoldingsSortField] = useState("market_value");
  const [holdingsSortOrder, setHoldingsSortOrder] = useState("desc");
  const [holdingsActiveTab, setHoldingsActiveTab] = useState("with_value");
  const [holdingsWithValueCount, setHoldingsWithValueCount] = useState(0);
  const [holdingsWithoutValueCount, setHoldingsWithoutValueCount] = useState(0);
  
  // Reconciliation job state
  const [runningJob, setRunningJob] = useState(false);
  const [rerunningMismatches, setRerunningMismatches] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("KARVY"); // Default to KARVY since CAMS txns not uploaded

  // ============ PAN Mismatch State (KYC Mismatch Engine) ============
  const [panMismatches, setPanMismatches] = useState([]);
  const [panMismatchLoading, setPanMismatchLoading] = useState(false);
  const [panMismatchTotal, setPanMismatchTotal] = useState(0);
  const [panMismatchStats, setPanMismatchStats] = useState({});
  const [panMismatchClusters, setPanMismatchClusters] = useState([]);
  const [panMismatchPage, setPanMismatchPage] = useState(1);
  const [panMismatchPageSize] = useState(50);
  const [panMismatchSearch, setPanMismatchSearch] = useState("");
  const [panRuleGroupFilter, setPanRuleGroupFilter] = useState(null);
  const [panIssueTypeFilter, setPanIssueTypeFilter] = useState(null);
  const [panConfidenceFilter, setPanConfidenceFilter] = useState(null);
  const [expandedMismatchIdx, setExpandedMismatchIdx] = useState(null);
  const [showClusters, setShowClusters] = useState(false);
  const [panComboFilter, setPanComboFilter] = useState(null); // Exact-combination capsule filter
  const [panBalanceTab, setPanBalanceTab] = useState("with"); // Default to with balance
  const [panWithBalanceCount, setPanWithBalanceCount] = useState(0);
  const [panWithoutBalanceCount, setPanWithoutBalanceCount] = useState(0);

  // ============ KYC Missing State (Missing KYC fields analysis) ============
  const [kycMissingRecords, setKycMissingRecords] = useState([]);
  const [kycMissingLoading, setKycMissingLoading] = useState(false);
  const [kycMissingTotal, setKycMissingTotal] = useState(0);
  const [kycMissingTotalRecords, setKycMissingTotalRecords] = useState(0); // Unfiltered total for stats
  const [kycMissingPage, setKycMissingPage] = useState(1);
  const [kycMissingPageSize] = useState(50);
  const [kycMissingSearch, setKycMissingSearch] = useState("");
  const [kycMissingWithBalanceCount, setKycMissingWithBalanceCount] = useState(0);
  const [kycMissingWithoutBalanceCount, setKycMissingWithoutBalanceCount] = useState(0);
  const [kycMissingBalanceTab, setKycMissingBalanceTab] = useState("with"); // "with" or "without"
  const [kycMissingActiveFilter, setKycMissingActiveFilter] = useState(null); // No default filter - show all records
  const [kycMissingSingleMissing, setKycMissingSingleMissing] = useState({});
  const [kycMissingComboMissing, setKycMissingComboMissing] = useState({});
  const [kycMissingFieldInfo, setKycMissingFieldInfo] = useState([]);
  const [kycMissingUniqueFolios, setKycMissingUniqueFolios] = useState(0);
  const [kycMissingExporting, setKycMissingExporting] = useState(false);

  // ============ Unit Mismatch tab REMOVED (2026-02-XX) ============
  // Per user request, Unit Mismatch tab and all its logic removed from
  // the Mismatch Analysis page. The Portfolio > Unit Mismatch tab is the
  // sole surviving surface. Backend endpoints
  // (`/api/reconciliation/clients-with-unit-mismatch`,
  // `/api/reconciliation/unit-mismatch-cache/refresh`,
  // `/api/admin/refresh-all-caches`) remain live for Portfolio consumption
  // and for the daily nightly rebuild.

  // ============ KYC Missing Functions (Missing KYC fields analysis) ============
  const fetchKycMissing = useCallback(async () => {
    if (!api) return;
    setKycMissingLoading(true);
    try {
      const params = new URLSearchParams({
        page: kycMissingPage.toString(),
        page_size: kycMissingPageSize.toString()
      });
      if (kycMissingSearch) params.append("search", kycMissingSearch);
      // Apply balance filter based on tab
      if (kycMissingBalanceTab === "with") {
        params.append("with_balance", "true");
      } else if (kycMissingBalanceTab === "without") {
        params.append("with_balance", "false");
      }
      if (kycMissingActiveFilter) params.append("missing_filter", kycMissingActiveFilter);
      
      const response = await api.get(`/reconciliation/kyc-missing?${params}`);
      setKycMissingRecords(response.data.records || []);
      setKycMissingTotal(response.data.total || 0);
      setKycMissingTotalRecords(response.data.total_records || 0); // Unfiltered total for stats
      setKycMissingWithBalanceCount(response.data.with_balance_count || 0);
      setKycMissingWithoutBalanceCount(response.data.without_balance_count || 0);
      setKycMissingSingleMissing(response.data.single_missing || {});
      setKycMissingComboMissing(response.data.combo_missing || {});
      setKycMissingFieldInfo(response.data.field_info || []);
      setKycMissingUniqueFolios(response.data.unique_folios || 0);
    } catch (error) {
      console.error("Failed to fetch KYC missing records:", error);
      toast.error("Failed to load KYC Missing data");
    } finally {
      setKycMissingLoading(false);
    }
  }, [api, kycMissingPage, kycMissingPageSize, kycMissingSearch, kycMissingBalanceTab, kycMissingActiveFilter]);

  // Export KYC Missing to Excel
  const exportKycMissingToExcel = async () => {
    setKycMissingExporting(true);
    try {
      const params = new URLSearchParams();
      if (kycMissingBalanceTab === "with") {
        params.append("with_balance", "true");
      } else if (kycMissingBalanceTab === "without") {
        params.append("with_balance", "false");
      }
      if (kycMissingActiveFilter) params.append("missing_filter", kycMissingActiveFilter);
      
      const response = await fetch(`${API_URL}/api/reconciliation/kyc-missing/export/excel?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Export failed");
      
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition");
      let filename = `kyc_missing_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("KYC Missing export downloaded");
    } catch (error) {
      console.error("KYC Missing export failed:", error);
      toast.error("Failed to export KYC Missing data");
    } finally {
      setKycMissingExporting(false);
    }
  };

  // Handle capsule filter click
  const handleKycMissingFilterClick = (filterKey) => {
    if (kycMissingActiveFilter === filterKey) {
      setKycMissingActiveFilter(null); // Toggle off
    } else {
      setKycMissingActiveFilter(filterKey);
    }
    setKycMissingPage(1);
  };

  // Get capsule filters for display (now using exact combinations from combo_missing)
  const getKycMissingFilters = () => {
    const filters = [];
    
    // All exact combinations are now in combo_missing
    Object.entries(kycMissingComboMissing).forEach(([key, count]) => {
      if (count > 0) {
        const parts = key.split("_");
        const labels = parts.map(p => {
          const info = kycMissingFieldInfo.find(f => f.key === p);
          return info?.label || p.toUpperCase();
        });
        
        // Color based on number of missing fields
        let bg, activeBg;
        if (parts.length === 1) {
          bg = "bg-red-50 text-red-700 border-red-200";
          activeBg = "bg-red-600 text-white border-red-600";
        } else if (parts.length <= 3) {
          bg = "bg-orange-50 text-orange-700 border-orange-200";
          activeBg = "bg-orange-600 text-white border-orange-600";
        } else {
          bg = "bg-amber-50 text-amber-700 border-amber-200";
          activeBg = "bg-amber-600 text-white border-amber-600";
        }
        
        filters.push({
          key,
          label: labels.join(" + "),
          count,
          bg,
          activeBg
        });
      }
    });
    
    return filters.sort((a, b) => b.count - a.count);
  };

  // Fetch KYC Missing data when tab is active
  useEffect(() => {
    if (activeTab === "kyc_missing") {
      fetchKycMissing();
    }
  }, [activeTab, fetchKycMissing]);

  // Fetch PAN Mismatch data (KYC Mismatch Engine)
  const fetchPanMismatch = useCallback(async () => {
    setPanMismatchLoading(true);
    try {
      const params = new URLSearchParams({
        page: panMismatchPage.toString(),
        page_size: panMismatchPageSize.toString(),
      });
      if (panMismatchSearch) params.append("search", panMismatchSearch);
      if (panRuleGroupFilter) params.append("rule_group", panRuleGroupFilter.toString());
      if (panIssueTypeFilter) params.append("issue_type", panIssueTypeFilter);
      if (panConfidenceFilter) params.append("confidence", panConfidenceFilter);
      if (panComboFilter) params.append("mismatch_filter", panComboFilter);
      if (panBalanceTab === "with") params.append("with_balance", "true");
      else if (panBalanceTab === "without") params.append("with_balance", "false");

      const response = await api.get(`/reconciliation/kyc-mismatch/pan?${params}`);
      setPanMismatches(response.data.mismatches || []);
      setPanMismatchTotal(response.data.total || 0);
      setPanMismatchStats(response.data.stats || {});
      setPanMismatchClusters(response.data.clusters || []);
      setPanWithBalanceCount(response.data.with_balance_count || 0);
      setPanWithoutBalanceCount(response.data.without_balance_count || 0);
    } catch (error) {
      console.error("Failed to fetch PAN mismatches:", error);
      toast.error("Failed to load PAN mismatch data");
    } finally {
      setPanMismatchLoading(false);
    }
  }, [api, panMismatchPage, panMismatchPageSize, panMismatchSearch, panRuleGroupFilter, panIssueTypeFilter, panConfidenceFilter, panComboFilter, panBalanceTab]);

  // Fetch PAN Mismatch when tab is active
  useEffect(() => {
    if (activeTab === "kyc_mismatch") {
      fetchPanMismatch();
    }
  }, [activeTab, fetchPanMismatch]);

  // Handle PAN combo capsule filter click
  const handlePanComboFilterClick = (filterKey) => {
    if (panComboFilter === filterKey) {
      setPanComboFilter(null);
    } else {
      setPanComboFilter(filterKey);
    }
    // Clear all other filters to avoid conflicting empty results
    setPanConfidenceFilter(null);
    setPanRuleGroupFilter(null);
    setPanIssueTypeFilter(null);
    setPanBalanceTab(panBalanceTab); // Keep current balance tab
    setPanMismatchPage(1);
    setExpandedMismatchIdx(null);
  };

  // Build PAN mismatch combo capsule filters from stats
  const getPanMismatchComboFilters = () => {
    const comboData = panMismatchStats.combo_mismatches || {};
    const fieldLabels = {
      name: "Name", dob: "DOB", email: "Email", phone: "Phone", address: "Address",
      invalid_pan: "Invalid PAN", pan_typo: "PAN Typo", duplicate_pan: "Duplicate PAN",
      high_duplicate: "High Duplicate", twins: "Possible Twins",
    };
    const filters = [];
    Object.entries(comboData).forEach(([key, count]) => {
      if (count > 0) {
        const parts = key.split("_");
        // Handle multi-word keys (invalid_pan, pan_typo, duplicate_pan, high_duplicate)
        const knownMultiWord = { invalid_pan: "Invalid PAN", pan_typo: "PAN Typo", duplicate_pan: "Duplicate PAN", high_duplicate: "High Duplicate" };
        let label;
        if (knownMultiWord[key]) {
          label = knownMultiWord[key];
        } else {
          label = parts.map(p => fieldLabels[p] || p.charAt(0).toUpperCase() + p.slice(1)).join(" + ");
        }
        let bg, activeBg;
        if (parts.length === 1 || knownMultiWord[key]) {
          bg = "bg-red-50 text-red-700 border-red-200";
          activeBg = "bg-red-600 text-white border-red-600";
        } else if (parts.length <= 3) {
          bg = "bg-orange-50 text-orange-700 border-orange-200";
          activeBg = "bg-orange-600 text-white border-orange-600";
        } else {
          bg = "bg-amber-50 text-amber-700 border-amber-200";
          activeBg = "bg-amber-600 text-white border-amber-600";
        }
        filters.push({ key, label, count, bg, activeBg });
      }
    });
    return filters.sort((a, b) => b.count - a.count);
  };

  // Rerun mismatches only
  const rerunMismatches = async () => {
    setRerunningMismatches(true);
    try {
      const response = await fetch(`${API_URL}/api/reconciliation/rerun-mismatches`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (!response.ok) {
        throw new Error("Failed to rerun mismatches");
      }
      
      const result = await response.json();
      toast.success(`Rerun complete: ${result.resolved} resolved, ${result.still_mismatched} still mismatched`);
      
      // Refresh the data
      await fetchData();
    } catch (error) {
      console.error("Rerun error:", error);
      toast.error("Failed to rerun mismatches");
    } finally {
      setRerunningMismatches(false);
    }
  };

  // Fetch mismatch data - Unit reconciliation for all clients
  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (mismatchTypeFilter) {
        params.append("mismatch_type", mismatchTypeFilter);
      }
      const response = await fetch(`${API_URL}/api/reconciliation/units/all/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching unit reconciliation data:", error);
    } finally {
      setLoading(false);
    }
  }, [token, mismatchTypeFilter]);
  
  // Run reconciliation job
  const runReconciliationJob = async () => {
    if (!token) return;
    setRunningJob(true);
    try {
      const response = await fetch(`${API_URL}/api/cronjobs/reconciliation/run`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Reconciliation job started. This may take a few minutes.");
        // Poll for completion
        const pollInterval = setInterval(async () => {
          const statusRes = await fetch(`${API_URL}/api/cronjobs/reconciliation/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (statusRes.ok) {
            const status = await statusRes.json();
            if (status.latest_job?.status === 'completed') {
              clearInterval(pollInterval);
              setRunningJob(false);
              toast.success("Reconciliation completed!");
              fetchData();
            } else if (status.latest_job?.status === 'failed') {
              clearInterval(pollInterval);
              setRunningJob(false);
              toast.error("Reconciliation job failed: " + (status.latest_job?.error || "Unknown error"));
            }
          }
        }, 3000);
        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (runningJob) {
            setRunningJob(false);
            toast.info("Job is still running. Check back later.");
          }
        }, 300000);
      } else {
        toast.error("Failed to start reconciliation job");
        setRunningJob(false);
      }
    } catch (error) {
      console.error("Error starting reconciliation job:", error);
      toast.error("Failed to start reconciliation job");
      setRunningJob(false);
    }
  };

  // ============ Missing Data (Holdings) Functions ============
  const fetchHoldings = useCallback(async () => {
    if (!api) return;
    setHoldingsLoading(true);
    try {
      const params = new URLSearchParams({
        skip: ((holdingsPage - 1) * holdingsPageSize).toString(),
        limit: holdingsPageSize.toString(),
        sort_by: holdingsSortField,
        sort_order: holdingsSortOrder,
      });
      
      if (holdingsSearchTerm) params.append("search", holdingsSearchTerm);
      if (holdingsActiveFilter && holdingsActiveFilter !== "all") {
        params.append("missing_combo", holdingsActiveFilter);
      }
      // When no filter is selected, backend defaults to excluding complete entries
      
      // Set filter based on active tab
      if (holdingsActiveTab === "with_value") {
        params.append("with_value_only", "true");
      } else {
        params.append("without_value_only", "true");
      }
      
      const response = await api.get(`/holdings?${params.toString()}`);
      setHoldings(response.data.holdings || []);
      setHoldingsTotalCount(response.data.total || 0);
      
      // Update tab counts
      if (response.data.with_value_count !== undefined) {
        setHoldingsWithValueCount(response.data.with_value_count);
      }
      if (response.data.without_value_count !== undefined) {
        setHoldingsWithoutValueCount(response.data.without_value_count);
      }
    } catch (error) {
      toast.error("Failed to load holdings");
    } finally {
      setHoldingsLoading(false);
    }
  }, [api, holdingsPage, holdingsPageSize, holdingsSearchTerm, holdingsActiveFilter, holdingsSortField, holdingsSortOrder, holdingsActiveTab]);

  const fetchHoldingsStats = useCallback(async () => {
    if (!api) return;
    try {
      const params = new URLSearchParams();
      if (holdingsActiveTab === "with_value") {
        params.append("with_value_only", "true");
      } else {
        params.append("without_value_only", "true");
      }
      const response = await api.get(`/holdings/stats?${params.toString()}`);
      setHoldingsStats(response.data);
      
      // Update tab counts - use "excluding complete" for the display
      if (response.data.with_value_excluding_complete !== undefined) {
        setHoldingsWithValueCount(response.data.with_value_excluding_complete);
      }
      if (response.data.without_value_excluding_complete !== undefined) {
        setHoldingsWithoutValueCount(response.data.without_value_excluding_complete);
      }
    } catch (error) {
      console.error("Failed to load stats", error);
    }
  }, [api, holdingsActiveTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === "missing_data") {
      fetchHoldings();
      fetchHoldingsStats();
    }
  }, [activeTab, fetchHoldings, fetchHoldingsStats]);

  // Filter mismatches based on search
  const filteredMismatches = data?.mismatches?.filter(item => 
    item.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.scheme_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.folio_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.client_pan?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Sort mismatches
  const sortedMismatches = [...filteredMismatches].sort((a, b) => {
    if (sortConfig.key === "client_name" || sortConfig.key === "scheme_name") {
      return sortConfig.direction === "asc" 
        ? (a[sortConfig.key] || "").localeCompare(b[sortConfig.key] || "")
        : (b[sortConfig.key] || "").localeCompare(a[sortConfig.key] || "");
    }
    const aVal = Math.abs(a[sortConfig.key] || 0);
    const bVal = Math.abs(b[sortConfig.key] || 0);
    return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  const SortableHeader = ({ field, children }) => (
    <TableHead 
      className="cursor-pointer hover:bg-gray-100 text-sm font-medium"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortConfig.key === field && (
          <span className="text-blue-600">{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
        )}
      </div>
    </TableHead>
  );

  // Toggle expanded row
  const toggleRowExpansion = (rowKey) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  // Export to Excel with raw transactions
  const exportToExcel = () => {
    if (!data?.mismatches || data.mismatches.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Summary sheet data
    const summaryData = data.mismatches.map(item => ({
      "Client Name": item.client_name,
      "PAN": item.client_pan,
      "Folio No": item.folio_no,
      "Scheme Name": item.scheme_name,
      "ISIN": item.isin,
      "Units Bought": item.units_bought,
      "Units Sold": item.units_sold,
      "Net (Transactions)": item.net_from_transactions,
      "Holdings (MFSD203)": item.balance_from_holdings,
      "Difference": item.difference,
      "Reason": item.reason,
      "Total Txns": item.transaction_count,
      "Junked Txns": item.junked_count
    }));

    // Raw transactions sheet data
    const txnData = [];
    data.mismatches.forEach(item => {
      if (item.raw_transactions) {
        item.raw_transactions.forEach(txn => {
          txnData.push({
            "Client Name": item.client_name,
            "PAN": item.client_pan,
            "Folio No": item.folio_no,
            "Transaction No": txn.transaction_no,
            "TD_TRTYPE": txn.TD_TRTYPE,
            "PR_TYPE": txn.PR_TYPE,
            "Units": txn.units,
            "Amount": txn.amount,
            "NAV": txn.nav,
            "Transaction Date": txn.transaction_date,
            "Transaction Desc": txn.transaction_desc,
            "Scheme Name": txn.scheme_name,
            "Is Junked": txn.is_junked ? "Yes" : "No",
            "Junk Reason": txn.junk_reason || ""
          });
        });
      }
    });

    // Create styled workbook via the shared exporter (auto column widths,
    // bold/teal header, zebra striping, Indian number format, P/L colouring,
    // dates `dd mmm yyyy`, empty cells as "—").
    const sheets = [{ name: "Mismatch Summary", rows: summaryData }];
    if (txnData.length > 0) sheets.push({ name: "Raw MFSD201 Transactions", rows: txnData });
    const fileName = `Unit_Reconciliation_Mismatches_${new Date().toISOString().split('T')[0]}.xlsx`;
    exportStyledXlsx(sheets, fileName);
    toast.success(`Exported ${summaryData.length} mismatches with ${txnData.length} transactions`);
  };

  // ============ Missing Data (Holdings) Handlers ============
  const handleHoldingsSearch = () => {
    setHoldingsPage(1);
    fetchHoldings();
  };

  const handleHoldingsFilterClick = (filter) => {
    setHoldingsActiveFilter(filter);
    setHoldingsPage(1);
  };

  const handleHoldingsSort = (field) => {
    if (holdingsSortField === field) {
      setHoldingsSortOrder(holdingsSortOrder === "asc" ? "desc" : "asc");
    } else {
      setHoldingsSortField(field);
      setHoldingsSortOrder("asc");
    }
    setHoldingsPage(1);
  };

  const HoldingsSortIcon = ({ field }) => {
    if (holdingsSortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return holdingsSortOrder === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-teal-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-teal-600" />;
  };

  const HoldingsSortableHeader = ({ field, children, className = "" }) => (
    <TableHead 
      className={`text-sm font-sans cursor-pointer hover:bg-gray-50 select-none`}
      onClick={() => handleHoldingsSort(field)}
    >
      <div className={`flex items-center ${className}`}>
        {children}
        <HoldingsSortIcon field={field} />
      </div>
    </TableHead>
  );

  const holdingsTotalPages = Math.ceil(holdingsTotalCount / holdingsPageSize);
  const c = holdingsStats.completeness || {};
  const singleMissing = holdingsStats.single_missing || {};
  const comboMissing = holdingsStats.combo_missing || {};
  const mandatoryFields = holdingsStats.mandatory_fields || [];

  // "All" count uses the pre-calculated value from stats API (already excludes complete for current tab)
  const allExcludingComplete = holdingsStats.all_excluding_complete || 0;

  // Color palette for dynamic field badges
  const colorPalette = [
    { bg: "bg-amber-50 text-amber-700 border-amber-200", activeBg: "bg-amber-100 text-amber-800 border-amber-400" },
    { bg: "bg-orange-50 text-orange-700 border-orange-200", activeBg: "bg-orange-100 text-orange-800 border-orange-400" },
    { bg: "bg-violet-50 text-violet-700 border-violet-200", activeBg: "bg-violet-100 text-violet-800 border-violet-400" },
    { bg: "bg-sky-50 text-sky-700 border-sky-200", activeBg: "bg-sky-100 text-sky-800 border-sky-400" },
    { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", activeBg: "bg-emerald-100 text-emerald-800 border-emerald-400" },
    { bg: "bg-rose-50 text-rose-700 border-rose-200", activeBg: "bg-rose-100 text-rose-800 border-rose-400" },
    { bg: "bg-pink-50 text-pink-700 border-pink-200", activeBg: "bg-pink-100 text-pink-800 border-pink-400" },
    { bg: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", activeBg: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-400" },
    { bg: "bg-purple-50 text-purple-700 border-purple-200", activeBg: "bg-purple-100 text-purple-800 border-purple-400" },
    { bg: "bg-indigo-50 text-indigo-700 border-indigo-200", activeBg: "bg-indigo-100 text-indigo-800 border-indigo-400" },
    { bg: "bg-cyan-50 text-cyan-700 border-cyan-200", activeBg: "bg-cyan-100 text-cyan-800 border-cyan-400" },
    { bg: "bg-teal-50 text-teal-700 border-teal-200", activeBg: "bg-teal-100 text-teal-800 border-teal-400" },
    { bg: "bg-lime-50 text-lime-700 border-lime-200", activeBg: "bg-lime-100 text-lime-800 border-lime-400" },
    { bg: "bg-yellow-50 text-yellow-700 border-yellow-200", activeBg: "bg-yellow-100 text-yellow-800 border-yellow-400" },
  ];

  // Build dynamic filters from API response
  const holdingsFilters = [
    { key: "all", label: "All", count: allExcludingComplete, bg: "bg-slate-100 text-slate-700 border-slate-300", activeBg: "bg-slate-200 text-slate-800 border-slate-500" },
  ];

  // Add single-field missing filters (only show if count > 0)
  Object.entries(singleMissing).forEach(([fieldKey, count], idx) => {
    if (count > 0) {
      const colors = colorPalette[idx % colorPalette.length];
      const label = fieldKey.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      holdingsFilters.push({
        key: fieldKey,
        label: label.length > 12 ? label.slice(0, 10) + "..." : label,
        fullLabel: label,
        count,
        ...colors
      });
    }
  });

  // Add combo filters (2-field combinations, only show if count > 0)
  Object.entries(comboMissing).forEach(([comboKey, count], idx) => {
    if (count > 0) {
      const parts = comboKey.split("_");
      // Only show 2-field combinations in the main filter bar
      if (parts.length === 2) {
        const colors = colorPalette[(idx + 5) % colorPalette.length];
        const label = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1, 3)).join("+");
        holdingsFilters.push({
          key: comboKey,
          label,
          fullLabel: parts.map(p => p.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())).join(" + "),
          count,
          ...colors
        });
      }
    }
  });

  // Legacy fallback - use completeness if single_missing is empty
  if (Object.keys(singleMissing).length === 0) {
    const legacyFilters = [
      { key: "pan", label: "PAN", count: c.pan, bg: "bg-amber-50 text-amber-700 border-amber-200", activeBg: "bg-amber-100 text-amber-800 border-amber-400" },
      { key: "mobile", label: "Mobile", count: c.mobile, bg: "bg-orange-50 text-orange-700 border-orange-200", activeBg: "bg-orange-100 text-orange-800 border-orange-400" },
      { key: "email", label: "Email", count: c.email, bg: "bg-violet-50 text-violet-700 border-violet-200", activeBg: "bg-violet-100 text-violet-800 border-violet-400" },
      { key: "pincode", label: "PIN", count: c.pincode, bg: "bg-sky-50 text-sky-700 border-sky-200", activeBg: "bg-sky-100 text-sky-800 border-sky-400" },
      { key: "pan_mobile", label: "PAN+Mob", count: c.pan_mobile, bg: "bg-rose-50 text-rose-700 border-rose-200", activeBg: "bg-rose-100 text-rose-800 border-rose-400" },
      { key: "pan_email", label: "PAN+Email", count: c.pan_email, bg: "bg-pink-50 text-pink-700 border-pink-200", activeBg: "bg-pink-100 text-pink-800 border-pink-400" },
    ];
    legacyFilters.forEach(f => {
      if (f.count > 0) holdingsFilters.push(f);
    });
  }

  // Additional mandatory field categories (from API)
  const additionalMissing = holdingsStats.additional_missing || {};
  const additionalFilters = Object.entries(additionalMissing)
    .filter(([_, count]) => count > 0)
    .map(([key, count], idx) => ({
      key,
      label: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
      count,
      ...colorPalette[(idx + 10) % colorPalette.length],
      tooltip: `Missing ${key.replace(/_/g, " ")}`
    }));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            Mismatch Analysis
          </h1>
          <p className="text-gray-500 mt-1">
            Data quality issues and reconciliation mismatches
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={
              activeTab === "kyc_missing"
                ? fetchKycMissing
                : activeTab === "kyc_mismatch"
                ? fetchPanMismatch
                : fetchData
            } 
            disabled={loading || kycMissingLoading || panMismatchLoading} 
            variant="outline"
            data-testid="mismatch-refresh-button"
            title="Reload data"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${(loading || kycMissingLoading || panMismatchLoading) ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="kyc_mismatch" className="data-[state=active]:bg-white" data-testid="kyc-mismatch-tab">
            <AlertCircle className="h-4 w-4 mr-2" />
            KYC Mismatch
            {(panMismatchStats.total_issues || 0) > 0 && (
              <span className="ml-1 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">{panMismatchStats.total_issues}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="kyc_missing" className="data-[state=active]:bg-white" data-testid="kyc-missing-tab">
            <AlertTriangle className="h-4 w-4 mr-2 text-red-500" />
            KYC Missing
            {kycMissingTotal > 0 && (
              <span className="ml-1 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">{kycMissingTotal}</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ==================== KYC MISMATCH TAB (PAN Analysis Engine) ==================== */}
        <TabsContent value="kyc_mismatch" className="space-y-4" data-testid="kyc-mismatch-content">

          {/* Tabs for With/Without Balance */}
          <Tabs value={panBalanceTab} onValueChange={(v) => { setPanBalanceTab(v); setPanMismatchPage(1); setPanComboFilter(null); setExpandedMismatchIdx(null); }} className="w-full">
            <TabsList className="grid w-96 grid-cols-2" data-testid="pan-balance-tabs">
              <TabsTrigger value="with" className="font-sans text-sm" data-testid="pan-balance-with">
                With Balance ({panWithBalanceCount.toLocaleString()})
              </TabsTrigger>
              <TabsTrigger value="without" className="font-sans text-sm" data-testid="pan-balance-without">
                Without Balance ({panWithoutBalanceCount.toLocaleString()})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Exact Combination Capsules (permutation filters) */}
          {(() => {
            const comboFilters = getPanMismatchComboFilters();
            if (comboFilters.length === 0) return null;
            return (
              <div className="space-y-1.5">
                <p className="text-xs text-gray-500 font-medium">Filter by Exact Mismatch Combination:</p>
                <div className="flex flex-wrap gap-1.5" data-testid="pan-combo-capsules">
                  {comboFilters.map((f) => (
                    <button
                      key={f.key}
                      data-testid={`pan-combo-${f.key}`}
                      onClick={() => handlePanComboFilterClick(f.key)}
                      className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-all ${
                        panComboFilter === f.key
                          ? `${f.activeBg} ring-1 ring-offset-1`
                          : `${f.bg} hover:brightness-95`
                      }`}
                    >
                      {f.label} ({f.count})
                    </button>
                  ))}
                  {(panComboFilter || panConfidenceFilter) && (
                    <button
                      data-testid="pan-combo-clear"
                      className="px-2.5 py-1 rounded-full border text-xs text-gray-500 border-gray-300 hover:bg-gray-100"
                      onClick={() => { setPanComboFilter(null); setPanConfidenceFilter(null); setPanMismatchPage(1); setExpandedMismatchIdx(null); }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Search */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 relative min-w-[250px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by Name, PAN..."
                className="pl-8 h-9 font-sans text-sm"
                value={panMismatchSearch}
                onChange={(e) => setPanMismatchSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchPanMismatch()}
                data-testid="pan-mismatch-search"
              />
            </div>
            <Button onClick={fetchPanMismatch} size="sm" className="font-sans" data-testid="pan-mismatch-search-btn">Search</Button>
          </div>

          {/* Clusters Section (toggle) */}
          {/* Results Table */}
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Mismatch Results</CardTitle>
                <div className="flex items-center gap-2">
                  {panConfidenceFilter && (
                    <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                      Confidence: {panConfidenceFilter}
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-sm bg-red-50 text-red-700 border-red-200">
                    {panMismatchTotal} results
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {panMismatchLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  <span className="ml-3 text-gray-500">Running PAN analysis engine...</span>
                </div>
              ) : panMismatches.length === 0 ? (
                <div className="text-center py-12" data-testid="pan-mismatch-empty">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No Issues Found</p>
                  <p className="text-gray-400 text-sm">
                    {panIssueTypeFilter || panRuleGroupFilter || panConfidenceFilter
                      ? "No results match the selected filters"
                      : "All investor PAN records are consistent"}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="py-2 text-xs text-gray-600 w-8"></TableHead>
                          <TableHead className="py-2 text-xs text-gray-600">PAN 1</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600">Name 1</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600">PAN 2</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600">Name 2</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600">DOB</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600 text-center">Score</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600">Issue Type</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600 text-right">Market Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {panMismatches.map((m, idx) => {
                          const issueColors = {
                            PAN_MATCH_NAME_MISMATCH: "bg-rose-50 text-rose-700 border-rose-200",
                            PAN_MATCH_DOB_MISMATCH: "bg-purple-50 text-purple-700 border-purple-200",
                            PAN_EMAIL_MISMATCH: "bg-sky-50 text-sky-700 border-sky-200",
                            PAN_PHONE_MISMATCH: "bg-teal-50 text-teal-700 border-teal-200",
                            PAN_ADDRESS_MISMATCH: "bg-yellow-50 text-yellow-700 border-yellow-200",
                            INVALID_PAN_FORMAT: "bg-red-50 text-red-700 border-red-200",
                            POSSIBLE_DUPLICATE_PAN: "bg-orange-50 text-orange-700 border-orange-200",
                            LIKELY_PAN_TYPO: "bg-amber-50 text-amber-700 border-amber-200",
                            HIGH_PROBABLE_DUPLICATE: "bg-red-50 text-red-700 border-red-200",
                            POSSIBLE_TWINS: "bg-cyan-50 text-cyan-700 border-cyan-200",
                            POSSIBLE_MATCH_REVIEW: "bg-violet-50 text-violet-700 border-violet-200",
                          };
                          const issueLabels = {
                            PAN_MATCH_NAME_MISMATCH: "Name Mismatch",
                            PAN_MATCH_DOB_MISMATCH: "DOB Mismatch",
                            PAN_EMAIL_MISMATCH: "Email Mismatch",
                            PAN_PHONE_MISMATCH: "Phone Mismatch",
                            PAN_ADDRESS_MISMATCH: "Address Mismatch",
                            INVALID_PAN_FORMAT: "Invalid PAN",
                            POSSIBLE_DUPLICATE_PAN: "Duplicate PAN",
                            LIKELY_PAN_TYPO: "PAN Typo",
                            HIGH_PROBABLE_DUPLICATE: "High Duplicate",
                            POSSIBLE_TWINS: "Possible Twins",
                            POSSIBLE_MATCH_REVIEW: "Review Match",
                          };
                          const confColors = {
                            High: "bg-red-100 text-red-700 border-red-300",
                            Medium: "bg-amber-100 text-amber-700 border-amber-300",
                            Low: "bg-gray-100 text-gray-600 border-gray-300",
                          };
                          const isSamePan = m.pan_1 === m.pan_2;
                          const dobDisplay = m.dob_1 === m.dob_2 ? m.dob_1 : `${m.dob_1 || "-"} / ${m.dob_2 || "-"}`;

                          return (
                            <React.Fragment key={idx}>
                              <TableRow
                                className="text-sm hover:bg-indigo-50/20 cursor-pointer"
                                onClick={() => setExpandedMismatchIdx(expandedMismatchIdx === idx ? null : idx)}
                                data-testid={`pan-mismatch-row-${idx}`}
                              >
                                <TableCell className="py-2">
                                  {expandedMismatchIdx === idx
                                    ? <ChevronDown className="w-4 h-4 text-gray-500" />
                                    : <ChevronRight className="w-4 h-4 text-gray-500" />}
                                </TableCell>
                                <TableCell className="py-2 font-mono text-xs font-semibold text-red-700">{m.pan_1}</TableCell>
                                <TableCell className="py-2 max-w-[140px] truncate font-sans text-xs" title={m.name_1}>{toTitleCase(m.name_1) || "-"}</TableCell>
                                <TableCell className="py-2 font-mono text-xs font-semibold">
                                  {isSamePan ? <span className="text-gray-400">same</span> : <span className="text-red-700">{m.pan_2}</span>}
                                </TableCell>
                                <TableCell className="py-2 max-w-[140px] truncate font-sans text-xs" title={m.name_2}>
                                  {isSamePan ? <span className="text-gray-400">-</span> : (toTitleCase(m.name_2) || "-")}
                                </TableCell>
                                <TableCell className="py-2 text-xs">{dobDisplay || "-"}</TableCell>
                                <TableCell className="py-2 text-center">
                                  <span className="font-mono text-xs font-semibold">{m.match_score}%</span>
                                </TableCell>
                                <TableCell className="py-2">
                                  <Badge variant="outline" className={`text-xs whitespace-nowrap ${issueColors[m.issue_type] || "bg-gray-50 text-gray-700"}`}>
                                    {issueLabels[m.issue_type] || m.issue_type}
                                  </Badge>
                                </TableCell>
                                <TableCell className="py-2 text-right font-mono text-xs">
                                  {(m.market_value || m.total_market_value) ? `₹${Number(m.market_value || m.total_market_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : "-"}
                                </TableCell>
                              </TableRow>

                              {/* Expanded Detail Row */}
                              {expandedMismatchIdx === idx && (
                                <TableRow>
                                  <TableCell colSpan={9} className="bg-slate-50 p-4">
                                    <div className="space-y-3">
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                        <div><span className="text-gray-500">Rule Group:</span> <span className="font-medium">Group {m.rule_group}</span></div>
                                        <div><span className="text-gray-500">Folios:</span> <span className="font-medium">{m.folio_count}</span></div>
                                        <div><span className="text-gray-500">Match Score:</span> <span className="font-medium">{m.match_score}%</span></div>
                                      </div>
                                      {/* Issue-specific variant summary */}
                                      {m.details && (
                                        <div className="text-xs space-y-1 border-t pt-2">
                                          {m.details.unique_names && m.details.unique_names.length > 0 && (
                                            <div><span className="text-gray-500">Name Variants:</span> <span className="font-mono">{m.details.unique_names.map(n => toTitleCase(n)).join(" | ")}</span></div>
                                          )}
                                          {m.details.unique_dobs && m.details.unique_dobs.length > 0 && (
                                            <div><span className="text-gray-500">DOB Variants:</span> <span className="font-mono">{m.details.unique_dobs.join(" | ")}</span></div>
                                          )}
                                          {m.details.unique_emails && m.details.unique_emails.length > 0 && (
                                            <div><span className="text-gray-500">Email Variants:</span> <span className="font-mono">{m.details.unique_emails.join(" | ")}</span></div>
                                          )}
                                          {m.details.unique_mobiles && m.details.unique_mobiles.length > 0 && (
                                            <div><span className="text-gray-500">Mobile Variants:</span> <span className="font-mono">{m.details.unique_mobiles.join(" | ")}</span></div>
                                          )}
                                          {m.details.unique_pincodes && m.details.unique_pincodes.length > 0 && (
                                            <div><span className="text-gray-500">Pincode Variants:</span> <span className="font-mono">{m.details.unique_pincodes.join(" | ")}</span></div>
                                          )}
                                          {m.details.matching_emails && m.details.matching_emails.length > 0 && (
                                            <div><span className="text-gray-500">Matching Email:</span> <span className="font-mono text-green-700">{m.details.matching_emails.join(", ")}</span></div>
                                          )}
                                          {m.details.matching_mobiles && m.details.matching_mobiles.length > 0 && (
                                            <div><span className="text-gray-500">Matching Mobile:</span> <span className="font-mono text-green-700">{m.details.matching_mobiles.join(", ")}</span></div>
                                          )}
                                          {m.details.pan_similarity !== undefined && (
                                            <div><span className="text-gray-500">PAN Similarity:</span> <span className="font-mono">{m.details.pan_similarity}%</span></div>
                                          )}
                                          {m.details.invalid_pan && (
                                            <div><span className="text-gray-500">Invalid PAN:</span> <span className="font-mono text-red-700">{m.details.invalid_pan}</span> <span className="text-gray-400">(Expected: AAAAA9999A)</span></div>
                                          )}
                                        </div>
                                      )}
                                      {/* Folio-level details table */}
                                      {m.folios && m.folios.length > 0 && (() => {
                                        // Compute majority value for each field
                                        const fields = ['pan', 'name', 'dob', 'email', 'mobile', 'pincode'];
                                        const majority = {};
                                        for (const fld of fields) {
                                          const counts = {};
                                          for (const fo of m.folios) {
                                            const v = (fo[fld] || '').trim().toLowerCase();
                                            if (v && v !== '-') counts[v] = (counts[v] || 0) + 1;
                                          }
                                          let maxC = 0, majVal = null;
                                          for (const [val, cnt] of Object.entries(counts)) {
                                            if (cnt > maxC) { maxC = cnt; majVal = val; }
                                          }
                                          // Only flag minorities if there are 2+ distinct values
                                          majority[fld] = Object.keys(counts).length >= 2 ? { val: majVal, cnt: maxC } : null;
                                        }
                                        const isMinority = (fo, fld) => {
                                          if (!majority[fld]) return false;
                                          const v = (fo[fld] || '').trim().toLowerCase();
                                          if (!v || v === '-') return false;
                                          return v !== majority[fld].val;
                                        };
                                        const hl = "bg-red-100 text-red-800 font-semibold";

                                        return (
                                          <div className="border-t pt-3">
                                            <h4 className="text-xs font-semibold text-gray-700 mb-2">
                                              Folio Details ({m.folios.length} records)
                                              <span className="ml-2 font-normal text-red-600">Minority values highlighted</span>
                                            </h4>
                                            <div className="overflow-x-auto border rounded">
                                              <Table>
                                                <TableHeader>
                                                  <TableRow className="bg-gray-100">
                                                    <TableHead className="text-xs py-1.5 font-semibold">Folio No</TableHead>
                                                    <TableHead className="text-xs py-1.5 font-semibold min-w-[180px]">AMC Name</TableHead>
                                                    <TableHead className="text-xs py-1.5 font-semibold">PAN</TableHead>
                                                    <TableHead className="text-xs py-1.5 font-semibold">Name</TableHead>
                                                    <TableHead className="text-xs py-1.5 font-semibold">DOB</TableHead>
                                                    <TableHead className="text-xs py-1.5 font-semibold">Email</TableHead>
                                                    <TableHead className="text-xs py-1.5 font-semibold">Mobile</TableHead>
                                                    <TableHead className="text-xs py-1.5 font-semibold">Pincode</TableHead>
                                                    <TableHead className="text-xs py-1.5 font-semibold">Source</TableHead>
                                                    <TableHead className="text-xs py-1.5 font-semibold text-right">Market Value</TableHead>
                                                  </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                  {m.folios.map((f, fi) => (
                                                    <TableRow key={fi} className="text-xs hover:bg-white">
                                                      <TableCell className="py-1 font-mono">{f.folio_no || "-"}</TableCell>
                                                      <TableCell className="py-1 max-w-[200px]">
                                                        <div className="truncate" title={f.amc_name}>
                                                          {toTitleCase(f.amc_name) || "-"}
                                                        </div>
                                                      </TableCell>
                                                      <TableCell className={`py-1 font-mono font-semibold ${isMinority(f, 'pan') ? hl : 'text-red-700'}`}>{f.pan || "-"}</TableCell>
                                                      <TableCell className={`py-1 max-w-[140px] truncate ${isMinority(f, 'name') ? hl : ''}`} title={f.name}>{toTitleCase(f.name) || "-"}</TableCell>
                                                      <TableCell className={`py-1 ${isMinority(f, 'dob') ? hl : ''}`}>{f.dob || "-"}</TableCell>
                                                      <TableCell className={`py-1 max-w-[160px] truncate ${isMinority(f, 'email') ? hl : ''}`} title={f.email}>{f.email || "-"}</TableCell>
                                                      <TableCell className={`py-1 ${isMinority(f, 'mobile') ? hl : ''}`}>{f.mobile || "-"}</TableCell>
                                                      <TableCell className={`py-1 ${isMinority(f, 'pincode') ? hl : ''}`}>{f.pincode || "-"}</TableCell>
                                                      <TableCell className="py-1">
                                                        <Badge variant="outline" className={`text-xs ${f.source === 'CAMS' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>{f.source}</Badge>
                                                      </TableCell>
                                                      <TableCell className="py-1 text-right font-mono">
                                                        {f.market_value ? `₹${Number(f.market_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : "-"}
                                                      </TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-3 text-sm">
                    <span className="text-gray-500">
                      {((panMismatchPage - 1) * panMismatchPageSize) + 1}-{Math.min(panMismatchPage * panMismatchPageSize, panMismatchTotal)} of {panMismatchTotal.toLocaleString()}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="h-7" disabled={panMismatchPage === 1} onClick={() => setPanMismatchPage(p => p - 1)}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="px-2 text-gray-600">{panMismatchPage}/{Math.ceil(panMismatchTotal / panMismatchPageSize) || 1}</span>
                      <Button variant="outline" size="sm" className="h-7" disabled={panMismatchPage >= Math.ceil(panMismatchTotal / panMismatchPageSize)} onClick={() => setPanMismatchPage(p => p + 1)}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== KYC MISSING TAB (Missing KYC fields analysis) ==================== */}
        <TabsContent value="kyc_missing" className="space-y-4" data-testid="kyc-missing-content">
          {/* Tabs for With/Without Balance */}
          <Tabs value={kycMissingBalanceTab} onValueChange={(v) => { setKycMissingBalanceTab(v); setKycMissingPage(1); setKycMissingActiveFilter(null); }} className="w-full">
            <TabsList className="grid w-96 grid-cols-2">
              <TabsTrigger value="with" className="font-sans text-sm">
                With Balance ({kycMissingWithBalanceCount.toLocaleString()})
              </TabsTrigger>
              <TabsTrigger value="without" className="font-sans text-sm">
                Without Balance ({kycMissingWithoutBalanceCount.toLocaleString()})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Filter Capsules - Missing field combinations */}
          <div className="space-y-1.5">
            <p className="text-xs text-gray-500 font-medium">Filter by Missing Field Combination:</p>
            <div className="flex flex-wrap gap-1.5" data-testid="kyc-missing-capsules">
              {getKycMissingFilters().map((f) => (
                <button
                  key={f.key}
                  onClick={() => handleKycMissingFilterClick(f.key)}
                  data-testid={`kyc-filter-${f.key}`}
                  className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-all ${
                    kycMissingActiveFilter === f.key ? `${f.activeBg} ring-1 ring-offset-1` : `${f.bg} hover:brightness-95`
                  }`}
                >
                  {f.label} ({f.count?.toLocaleString() || 0})
                </button>
              ))}
              {kycMissingActiveFilter && (
                <button
                  data-testid="kyc-missing-clear-filter"
                  className="px-2.5 py-1 rounded-full border text-xs text-gray-500 border-gray-300 hover:bg-gray-100"
                  onClick={() => { setKycMissingActiveFilter(null); setKycMissingPage(1); }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <AlertTriangle className="inline h-4 w-4 mr-2 text-amber-600" />
            <strong>KYC Missing:</strong> Records with missing mandatory KYC fields. Data auto-updates when new MFSD211/WBR9 files are uploaded and Common Investor Master is regenerated.
          </div>

          {/* Summary Stats - Always show TOTAL counts (unfiltered) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600 font-medium">Total Records</p>
              <p className="text-2xl font-bold text-red-700">{kycMissingTotalRecords.toLocaleString()}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-600 font-medium">Unique Folios</p>
              <p className="text-2xl font-bold text-orange-700">{kycMissingUniqueFolios.toLocaleString()}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-sm text-slate-600 font-medium">Without Balance</p>
              <p className="text-2xl font-bold text-slate-700">{kycMissingWithoutBalanceCount.toLocaleString()}</p>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <p className="text-sm text-teal-600 font-medium">With Balance</p>
              <p className="text-2xl font-bold text-teal-700">{kycMissingWithBalanceCount.toLocaleString()}</p>
            </div>
          </div>

          {/* Search + Export */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 relative min-w-[250px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by Name, Folio, Product Code, Scheme..."
                className="pl-8 h-9 font-sans text-sm"
                value={kycMissingSearch}
                onChange={(e) => setKycMissingSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchKycMissing()}
                data-testid="kyc-missing-search"
              />
            </div>
            <Button onClick={fetchKycMissing} size="sm" className="font-sans" data-testid="kyc-missing-search-btn">
              Search
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={exportKycMissingToExcel}
              disabled={kycMissingExporting || kycMissingTotal === 0}
              data-testid="kyc-missing-export-btn"
            >
              {kycMissingExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export Excel
            </Button>
          </div>

          {/* KYC Missing Table */}
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">KYC Missing Records</CardTitle>
                <Badge variant="outline" className="text-sm bg-red-50 text-red-700 border-red-200">
                  {kycMissingTotal.toLocaleString()} records with missing KYC
                </Badge>
              </div>
              <CardDescription>
                Records identified by Product Code + Folio No with missing mandatory KYC fields
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {kycMissingLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-red-600" />
                  <span className="ml-3 text-gray-500">Loading KYC Missing records...</span>
                </div>
              ) : kycMissingRecords.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No KYC Missing Records</p>
                  <p className="text-gray-400 text-sm">
                    {kycMissingActiveFilter 
                      ? "No records match the selected filter" 
                      : "All investor records have complete KYC information"}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="py-2 text-xs text-gray-600">PAN</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600">Name</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600">Folio No</TableHead>
                          <TableHead className="py-2 text-xs text-gray-600">AMC Name</TableHead>
                          {!kycMissingActiveFilter && (
                            <TableHead className="py-2 text-xs text-gray-600">Missing</TableHead>
                          )}
                          <TableHead className="py-2 text-xs text-gray-600 text-right">Market Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {kycMissingRecords.map((record, idx) => (
                          <TableRow 
                            key={`${record.product_code}-${record.folio_no}-${idx}`}
                            className="hover:bg-red-50/30 text-sm font-sans"
                            data-testid={`kyc-missing-row-${record.folio_no}`}
                          >
                            <TableCell className="py-2 font-mono text-sm">
                              {record.first_holder_pan || (record.guardian_pan ? (
                                <span className="text-purple-600" title="Guardian PAN">{record.guardian_pan}</span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              ))}
                            </TableCell>
                            <TableCell className="py-2 font-sans max-w-[220px]">
                              <div className="truncate">
                                {toTitleCase(record.first_holder_name) || "-"}
                              </div>
                              {record.guardian_name && (
                                <span className="text-xs text-purple-600">(Guardian: {toTitleCase(record.guardian_name)})</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 font-mono text-sm">{record.folio_no || "-"}</TableCell>
                            <TableCell className="py-2 font-sans max-w-[200px]">
                              <div className="truncate" title={record.amc_name || record.scheme_name}>
                                {toTitleCase(record.amc_name) || toTitleCase(record.scheme_name) || "-"}
                              </div>
                            </TableCell>
                            {!kycMissingActiveFilter && (
                              <TableCell className="py-2">
                                <div className="flex flex-wrap gap-1">
                                  {(record.missing_fields || []).map(field => (
                                    <Badge 
                                      key={field} 
                                      variant="outline" 
                                      className="text-xs bg-red-50 text-red-600 border-red-200"
                                    >
                                      {field.toUpperCase()}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                            )}
                            <TableCell className="py-2 text-right font-sans font-medium text-teal-700">
                              {record.market_value 
                                ? `₹${record.market_value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` 
                                : <span className="text-gray-400">-</span>
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-3 text-sm">
                    <span className="text-gray-500">
                      {((kycMissingPage - 1) * kycMissingPageSize) + 1}-{Math.min(kycMissingPage * kycMissingPageSize, kycMissingTotal)} of {kycMissingTotal.toLocaleString()}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7" 
                        disabled={kycMissingPage === 1} 
                        onClick={() => setKycMissingPage(p => p - 1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="px-2 text-gray-600">
                        {kycMissingPage}/{Math.ceil(kycMissingTotal / kycMissingPageSize) || 1}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7" 
                        disabled={kycMissingPage >= Math.ceil(kycMissingTotal / kycMissingPageSize)} 
                        onClick={() => setKycMissingPage(p => p + 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
